import { ChangeDetectionStrategy, Component, ElementRef, Injector, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { UiComponent } from '@df/ui/ui.component';
import { PriceService } from '@df/ui/price/price.service';
import { NToggle, ToggleService } from '@df/ui/toggle/toggle.service';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { BehaviorSubject } from 'rxjs';
import { NCmsBranchCollection } from '../../branch/collection/branch-collection.component';
import { NCmsDelivery } from '../../common/delivery/modal/delivery-modal.component';
import { iconsOptions } from '../../styles/cms.config';
import { NPageSearch } from '../search/page-search.interfaces';
import { PageOffCanvasSearchFormComponent } from './search-form/page-off-canvas-search-form.component';

export namespace NPageOffCanvas {
    export const BLOCK_ID = 'off-canvas-menu';

    export const MENU_COMPONENT_ID = 'page-off-canvas-menu';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IMenu {
        /**
         * Off canvas menu
         */
        menu: IMenuItem[];
    }

    /**
     * Interface for menu component
     * We fake component data here to use the component nested
     */
    export interface IMenuComponentData extends IMenu, NICmsContentComponent.IData {}

    /**
     * Block data
     */
    export interface IBlockDataContent extends IMenu {
        bannerText?: string;
        bannerLink?: string;
        bannerImage?: string;
        bannerButtonCopy?: string;
        navigationMenu: IIconMenuItem[];
        hideTab?: boolean;
        firstActivated?: boolean;
        firstTabImage?: string;
        firstTabLink?: string;
        secondTabImage?: string;
        secondTabLink?: string;
    }

    /**
     * Main menu item
     */
    export interface IMenuItem {
        /**
         * Item type
         * either link or menu opener
         */
        type: EMenuItemType;
        icon?: string;
        image?: string;
        /**
         * Item label
         */
        text: string;
        /**
         * Sub-menu (EMenuItemType.menu only)
         */
        subItem: IMenu;
        /**
         * Item link (EMenuItemType.link only)
         */
        link: string;
    }

    /**
     * Bottom menu item
     */
    export interface IIconMenuItem {
        /**
         * Item label
         */
        icon?: string;
        /**
         * Item label
         */
        text: string;
        /**
         * Item link
         */
        link: string;
    }

    export interface IFeaturedItem {
        image: string;
        link: string;
        text: string;
    }

    export enum EMenuItemType {
        link,
        menu,
        button
    }

    const fields: NICmsContentField.IField[] = [
        <NICmsContentField.IChoiceField>{
            key: 'type',
            type: NICmsContentField.EFieldType.choice,
            title: 'Type',
            required: true,
            options: [
                { key: NPageOffCanvas.EMenuItemType.menu, title: 'Submenu' },
                { key: NPageOffCanvas.EMenuItemType.link, title: 'Link' },
                { key: NPageOffCanvas.EMenuItemType.button, title: 'Button' }
            ],
            default: NPageOffCanvas.EMenuItemType.link
        },
        <NICmsContentField.IChoiceField>{
            key: 'icon',
            type: NICmsContentField.EFieldType.choice,
            title: 'Icon',
            previewMode: 'icon',
            options: iconsOptions,
            canShow: (data: NPageOffCanvas.IMenuItem) => data?.type === NPageOffCanvas.EMenuItemType.menu
        },
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            canShow: (data: NPageOffCanvas.IMenuItem) => data?.type === NPageOffCanvas.EMenuItemType.link
        },
        {
            key: 'text',
            type: NICmsContentField.EFieldType.text,
            title: 'Item copy',
            default: 'Menu item',
            required: true
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            default: '/',
            required: true,
            canShow: (data: NPageOffCanvas.IMenuItem) => data?.type !== NPageOffCanvas.EMenuItemType.menu
        }
    ];
    const menuField = <NICmsContentField.IRepeaterField<IMenuItem>>{
        key: 'menu',
        type: NICmsContentField.EFieldType.repeater,
        title: 'Menu',
        itemName: 'Menu item',
        itemNameField: 'text',
        required: true,
        complex: true,
        minSize: 1,
        defaultSize: 0,
        // note that type will not defined for the top level component
        canShow: (data: NPageOffCanvas.IMenuItem) =>
            data?.type !== NPageOffCanvas.EMenuItemType.link && data?.type !== NPageOffCanvas.EMenuItemType.button,
        fields
    };
    fields.push(menuField);
    export const MENU_FIELDS = [menuField];

    export enum EOpenState {
        closed = 0,
        openMenu = 1,
        openSearch = 2
    }

    export const OFF_CANVAS_GROUP_ID = 'offCanvas';
}

@Component({
    selector: 'page-off-canvas',
    templateUrl: 'page-off-canvas.html',
    styleUrls: ['page-off-canvas.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageOffCanvasComponent>({
    id: NPageOffCanvas.BLOCK_ID,
    block: {
        url: NPageOffCanvas.BLOCK_ID,
        scope: 'website',
        autoload: 'prerender'
    },
    onDemandOnly: true,
    name: 'Mobile menu',
    description: 'Mobile menu block',
    fields: [
        ...NPageOffCanvas.MENU_FIELDS,
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'navigationMenu',
            title: 'Navigation Menu',
            itemName: 'Item',
            itemNameField: 'text',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Icon',
                    key: 'icon',
                    previewMode: 'icon',
                    options: iconsOptions
                },
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Label',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                }
            ]
        },

        { key: 'tabslabel', type: NICmsContentField.EFieldType.label, title: 'Brand tabs' },
        {
            key: 'hideTab',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide tab'
        },
        <NICmsContentField.IToggleField>{
            key: 'firstActivated',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Activated tab',
            labels: { true: 'First tab activated', false: 'Second tab activated' },
            default: true,
            canShow: (data: NPageOffCanvas.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'firstTabImage',
            type: NICmsContentField.EFieldType.image,
            title: 'First tab image',
            canShow: (data: NPageOffCanvas.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'firstTabLink',
            type: NICmsContentField.EFieldType.text,
            title: 'First tab link',
            canShow: (data: NPageOffCanvas.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'secondTabImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Second tab image',
            canShow: (data: NPageOffCanvas.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'secondTabLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Second tab link',
            canShow: (data: NPageOffCanvas.IBlockDataContent) => !data?.hideTab
        },
        <NICmsContentField.ITextField>{
            type: NICmsContentField.EFieldType.text,
            key: 'bannerText',
            title: 'Banner text'
        },
        <NICmsContentField.ITextField>{
            type: NICmsContentField.EFieldType.text,
            key: 'bannerLink',
            title: 'Banner link'
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'bannerImage',
            title: 'Banner image'
        },
        <NICmsContentField.ITextField>{
            type: NICmsContentField.EFieldType.text,
            key: 'bannerButtonCopy',
            title: 'Banner button copy'
        }
    ]
})
export class PageOffCanvasComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @Input()
    offCanvasState!: BehaviorSubject<NPageOffCanvas.EOpenState>;

    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPageOffCanvas.IBlockDataContent;

    /**
     * I failed with subcomponents
     */
    protected override canDetach = false;
    protected menuTitles: { [key: string]: string } = {};
    protected vatSelected = false;
    /**
     * Shift level
     */
    level = new BehaviorSubject(0);

    @ViewChild('stage', { read: ElementRef })
    stage?: ElementRef;

    @ViewChild('cmsNode', { read: ElementRef, static: false })
    node?: ElementRef<HTMLElement>;

    readonly toggleGroupId = NPageOffCanvas.OFF_CANVAS_GROUP_ID;
    readonly searchMeganavKey = NPageSearch.SEARCH_MEGANAV_KEY;
    protected BRANCH_MODAL_ID = NCmsBranchCollection.MODAL_ID;
    readonly DELIVERY_MODAL_ID = NCmsDelivery.MODAL_ID;

    constructor(
        private elementRef: ElementRef,
        private renderer2: Renderer2,
        private toggleService: ToggleService,
        protected app: App,
        private priceService: PriceService
    ) {
        super();
    }

    get searchToggleId(): NToggle.TId {
        return PageOffCanvasSearchFormComponent.toggleId;
    }

    get offCanvasMenuData(): NICmsContentComponent.IData {
        return Object.assign({}, this.content || {}, { id: NPageOffCanvas.MENU_COMPONENT_ID });
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.app.meta.routerEvents.subscribe(() => this.close()));
        const catalogPricesInclTax = !!this.priceService.showCatalogPricesInclTax;
        if (!!catalogPricesInclTax) {
            this.vatSelected = catalogPricesInclTax;
        }
    }

    /**
     * Close menu
     */
    close(deep = false) {
        const searchToggle = this.toggleService.getItem(this.searchToggleId);

        if (!deep && searchToggle.isOpen && this.offCanvasState.value === NPageOffCanvas.EOpenState.openMenu) {
            searchToggle.close();
        } else {
            if (this.offCanvasState.value) {
                this.offCanvasState.next(NPageOffCanvas.EOpenState.closed);
                this.setLevel(0);
            }
        }
    }

    /**
     * Updates level shift
     */
    setLevel(level: number) {
        this.level.next(level);
        this.renderer2.setStyle(this.stage?.nativeElement, 'transform', `translateX(-${100 * level}%)`);
        level === 0
            ? this.renderer2.removeClass(this.elementRef.nativeElement, '_isSubmenu')
            : this.renderer2.addClass(this.elementRef.nativeElement, '_isSubmenu');
        this.detectChanges();
    }

    back() {
        this.setLevel(this.level.getValue() - 1);
    }

    changeMenuTitle(title: string, level: number) {
        if (level !== 0 && this.menuTitles[level] !== title) {
            this.menuTitles[level] = title;
            this.detectChanges();
        }
    }

    protected toggleVat() {
        this.priceService.showCatalogPricesInclTax = this.vatSelected;
        this.detectChanges();
    }
}
